import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material'
import {
  ArrowLeft,
  ArrowRight,
  SkipForward,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  ExternalLink,
  Users,
  Sparkles,
  EyeOff,
  Send,
  Copy,
  Check,
} from 'lucide-react'
import {
  Platform,
  useGetListeningTopicFeedLazyQuery,
  useMarkTopicContentInteractedMutation,
  useHideTopicContentMutation,
  useSaveContentCommentMutation,
} from 'graphql/generated/graphql'
import { useNalapodExtension } from 'hooks/useNalapodExtension'
import { COMMENT_STYLES, CommentStyle } from 'constants/commentStyles'
import { LOCAL, DEVELOP } from 'constants/env'

const config = process.env.NODE_ENV === 'development' ? LOCAL : DEVELOP

const platformIcons: Record<Platform, string> = {
  [Platform.Instagram]: '📷',
  [Platform.TikTok]: '🎵',
  [Platform.Youtube]: '🎥',
  [Platform.Reddit]: '🔴',
  [Platform.Facebook]: '📘',
  [Platform.Twitter]: '🐦',
  [Platform.Bluesky]: '🦋',
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

interface EngagementQueueProps {
  topicId: string
  topicName: string
}

export default function EngagementQueue({ topicId, topicName }: EngagementQueueProps): JSX.Element {
  const [items, setItems] = useState<any[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [comment, setComment] = useState('')
  const [copied, setCopied] = useState(false)
  const [replying, setReplying] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [sessionCount, setSessionCount] = useState(0)
  const [selectedStyle, setSelectedStyle] = useState<CommentStyle>('casual')

  const [fetchFeed, { loading }] = useGetListeningTopicFeedLazyQuery()
  const [markInteracted] = useMarkTopicContentInteractedMutation()
  const [hideContent] = useHideTopicContentMutation()
  const [saveComment] = useSaveContentCommentMutation()
  const { openReply } = useNalapodExtension()

  // Save comment to DB (fire and forget)
  const persistComment = (contentId: string, text: string): void => {
    if (text.trim()) {
      saveComment({ variables: { contentId, comment: text.trim() } }).catch(console.error)
    }
  }

  const loadQueue = async (): Promise<void> => {
    const result = await fetchFeed({
      variables: { input: { topicId, limit: 50, offset: 0 } },
      fetchPolicy: 'network-only',
    })
    const feed = result.data?.getListeningTopicFeed?.content ?? []
    // Only show non-interacted items in the queue
    const uninteracted = feed.filter((c: any) => !c.interactedWith)
    setItems(uninteracted)
    setCurrentIndex(0)
    // Pre-fill the first comment
    if (uninteracted.length > 0 && uninteracted[0].preGeneratedComment) {
      setComment(uninteracted[0].preGeneratedComment)
    }
  }

  useEffect(() => {
    loadQueue().catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId])

  const currentItem = items[currentIndex] ?? null
  const totalRemaining = items.length - currentIndex

  const goToNext = (): void => {
    const nextIndex = currentIndex + 1
    if (nextIndex < items.length) {
      setCurrentIndex(nextIndex)
      setComment(items[nextIndex]?.preGeneratedComment ?? '')
    } else {
      // Past the last item — show "all caught up"
      setCurrentIndex(items.length)
      setComment('')
    }
    setCopied(false)
  }

  const handleSkip = (): void => {
    goToNext()
  }

  const handleHide = (): void => {
    if (!currentItem) return
    const contentId = currentItem.id
    // Remove from local queue — after filter, currentIndex now points to the next item
    setItems((prev) => {
      const next = prev.filter((_, i) => i !== currentIndex)
      // Pre-fill comment for the new current item (same index in the shorter array)
      const nextItem = next[currentIndex] ?? next[currentIndex - 1] ?? null
      setComment(nextItem?.preGeneratedComment ?? '')
      // If we removed the last item and nothing is at currentIndex, adjust
      if (currentIndex >= next.length && next.length > 0) {
        setCurrentIndex(next.length - 1)
      }
      return next
    })
    // Persist to backend
    hideContent({ variables: { contentId } }).catch(console.error)
    setCopied(false)
  }

  const handleCopy = (): void => {
    if (comment) {
      void navigator.clipboard.writeText(comment)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleReply = async (): Promise<void> => {
    if (!currentItem || !comment) return

    void navigator.clipboard.writeText(comment)
    setReplying(true)

    const isSupported = currentItem.contentUrl?.includes('instagram.com') || currentItem.contentUrl?.includes('tiktok.com')
    if (isSupported) {
      const ok = await openReply(currentItem.contentUrl, comment)
      if (!ok) {
        window.open(currentItem.contentUrl, '_blank')
      }
    } else {
      window.open(currentItem.contentUrl, '_blank')
    }

    setReplying(false)
    markInteracted({ variables: { contentId: currentItem.id } }).catch(console.error)
    setSessionCount((prev) => prev + 1)
    goToNext()
  }

  const handleRegenerate = async (): Promise<void> => {
    if (!currentItem) return
    setRegenerating(true)
    try {
      const response = await fetch(`${config.API_URL}/api/generate-comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          hashtags: currentItem.hashtags ?? [],
          summary: currentItem.summary ?? '',
          caption: currentItem.caption ?? '',
          selectedStyle,
        }),
      })
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.comment) {
          const newComment = data.comment.trim()
          setComment(newComment)
          persistComment(currentItem.id, newComment)
        }
      }
    } catch (error) {
      console.error('Error regenerating comment:', error)
    } finally {
      setRegenerating(false)
    }
  }

  if (loading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#a855f7' }} />
      </Box>
    )
  }

  if (!currentItem) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography sx={{ color: 'white', fontSize: '20px', fontWeight: 600, mb: 1 }}>
          All caught up
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 1 }}>
          You joined {sessionCount} conversations this session.
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
          New conversations will appear after the next daily scan.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 0, sm: 1 } }}>
      {/* Session stats bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
          {totalRemaining} conversations left
        </Typography>
        <Typography sx={{ color: '#a855f7', fontSize: '13px', fontWeight: 600 }}>
          {sessionCount} joined today
        </Typography>
      </Box>

      {/* Post card */}
      <Box sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', overflow: 'hidden', mb: 2 }}>
        {/* Platform + metrics header */}
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${platformIcons[currentItem.platform as Platform]} ${String(currentItem.platform)}`}
            size="small"
            sx={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'white', fontSize: '12px' }}
          />
          {currentItem.authorFollowerCount && (
            <Chip
              icon={<Users size={12} />}
              label={formatNumber(currentItem.authorFollowerCount)}
              size="small"
              sx={{ backgroundColor: 'rgba(168,85,247,0.1)', color: '#a855f7', fontSize: '11px', '& .MuiChip-icon': { color: '#a855f7' } }}
            />
          )}
          {currentItem.relevanceScore != null && (
            <Typography sx={{ ml: 'auto', color: 'rgba(168,85,247,0.7)', fontSize: '11px' }}>
              {Math.round(currentItem.relevanceScore * 100)}% relevant
            </Typography>
          )}
        </Box>

        {/* Author */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {currentItem.authorAvatarUrl && (
            <img src={currentItem.authorAvatarUrl} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: 'white', fontSize: '14px', fontWeight: 600 }}>
              @{currentItem.authorUsername}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
              {formatDate(currentItem.postedAt)}
            </Typography>
          </Box>
          <IconButton onClick={() => window.open(currentItem.contentUrl, '_blank')} sx={{ color: 'rgba(255,255,255,0.4)' }}>
            <ExternalLink size={16} />
          </IconButton>
        </Box>

        {/* Caption */}
        {currentItem.caption && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', lineHeight: 1.6 }}>
              {String(currentItem.caption).length > 500 ? String(currentItem.caption).slice(0, 500) + '...' : String(currentItem.caption)}
            </Typography>
          </Box>
        )}

        {/* Summary */}
        {currentItem.summary && currentItem.summary !== '[Summary unavailable]' && (
          <Box sx={{ mx: 2, mb: 2, p: 1.5, backgroundColor: 'rgba(168,85,247,0.06)', borderRadius: '10px', border: '1px solid rgba(168,85,247,0.12)' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', lineHeight: 1.5 }}>
              {currentItem.summary}
            </Typography>
          </Box>
        )}

        {/* Engagement metrics */}
        <Box sx={{ px: 2, pb: 2, display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Heart size={14} color="rgba(255,255,255,0.4)" /><Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{formatNumber(currentItem.likes)}</Typography></Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><MessageCircle size={14} color="rgba(255,255,255,0.4)" /><Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{formatNumber(currentItem.comments)}</Typography></Box>
          {currentItem.shares > 0 && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Share2 size={14} color="rgba(255,255,255,0.4)" /><Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{formatNumber(currentItem.shares)}</Typography></Box>}
          {currentItem.views && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Eye size={14} color="rgba(255,255,255,0.4)" /><Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{formatNumber(currentItem.views)}</Typography></Box>}
        </Box>
      </Box>

      {/* Comment section */}
      <Box sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', p: 2 }}>
        {/* Style selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Comment style
          </Typography>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={selectedStyle}
              onChange={(e) => setSelectedStyle(e.target.value as CommentStyle)}
              sx={{
                backgroundColor: 'rgba(168,85,247,0.05)',
                color: 'rgba(255,255,255,0.9)',
                fontSize: '12px',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(168,85,247,0.2)' },
                '& .MuiSelect-icon': { color: '#a855f7' },
              }}
            >
              {Object.entries(COMMENT_STYLES).map(([key, style]) => (
                <MenuItem key={key} value={key} sx={{ fontSize: '12px' }}>{style.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Editable comment */}
        <TextField
          fullWidth
          multiline
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => { if (currentItem) persistComment(currentItem.id, comment) }}
          placeholder="Your comment..."
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              color: 'white',
              fontSize: '14px',
              backgroundColor: 'rgba(255,255,255,0.03)',
              borderRadius: '10px',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
              '&:hover fieldset': { borderColor: 'rgba(168,85,247,0.3)' },
              '&.Mui-focused fieldset': { borderColor: '#a855f7' },
            },
          }}
        />

        {/* Action buttons — Reply full width on top, Regenerate + Copy side by side below */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            onClick={() => { handleReply().catch(console.error) }}
            disabled={replying || !comment.trim()}
            startIcon={replying ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <Send size={14} />}
            fullWidth
            sx={{ background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', color: 'white', borderRadius: '10px', textTransform: 'none', fontSize: '14px', fontWeight: 600, py: 1 }}
          >
            Reply
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => { handleRegenerate().catch(console.error) }}
              disabled={regenerating}
              startIcon={regenerating ? <CircularProgress size={14} /> : <Sparkles size={14} />}
              sx={{ color: '#a855f7', border: '1px solid rgba(168,85,247,0.2)', borderRadius: '10px', textTransform: 'none', fontSize: '12px', flex: 1 }}
            >
              Regenerate
            </Button>
            <Button
              onClick={handleCopy}
              startIcon={copied ? <Check size={14} /> : <Copy size={14} />}
              sx={{ color: copied ? '#22c55e' : 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', textTransform: 'none', fontSize: '12px', flex: 1 }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Skip + Hide buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
        <Button
          onClick={handleSkip}
          startIcon={<SkipForward size={14} />}
          sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none', fontSize: '13px', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}
        >
          Skip
        </Button>
        <Button
          onClick={handleHide}
          startIcon={<EyeOff size={14} />}
          sx={{ color: 'rgba(239,68,68,0.5)', textTransform: 'none', fontSize: '13px', '&:hover': { color: '#ef4444' } }}
        >
          Not relevant
        </Button>
      </Box>
    </Box>
  )
}
